import { useState } from "preact/hooks";
import { addDays, CHANNEL_LABEL, del, fmtDateTime, fmtDay, fmtTime, get, html, localDate, localDateTime, post, put, tr } from "../lib.js";
import { Badge, Empty, Field, Loading, Modal, PageHeader, toast, useAction, useLoad } from "../ui.js";
import { PageGuide } from "../guide.js";
import { bookingErrorText, canReschedule } from "../booking-moves.js";

const statusKind = { pending_payment: "warn", confirmed: "ok", completed: "neutral", no_show: "warn", cancelled: "muted" };

/** Display labels for booking statuses (the stored value stays English). */
const statusLabel = (s) =>
  ({ pending_payment: tr("awaiting deposit"), confirmed: tr("confirmed"), completed: tr("completed"), no_show: tr("no-show"), cancelled: tr("cancelled") })[s] ?? s;

/** What the check-in did about the previous visit's summary, for the toast. */
const CHECK_IN_TOAST = {
  sent: "Checked in. The customer got their last visit's summary and progress link",
  no_previous_summary: "Checked in. No earlier visit summary to send yet",
  already_sent: "The customer already got their last visit's summary",
  no_contact: "Checked in. No phone or chat on this booking to send the summary to",
  paused: "Checked in. Automated messages are paused, so nothing was sent",
  not_reached: "Checked in, but the summary couldn't reach the customer",
};

/** Upcoming in the next two days and still not confirmed by the customer: worth a call. */
const unconfirmedSoon = (b, now) => b.status === "confirmed" && !b.is_test && !b.customer_confirmed_at && Date.parse(b.start_at) > now && Date.parse(b.start_at) - now < 2 * 86_400_000;

/**
 * Made in the AI agent page's test chat (#182): it holds the time, but nobody is coming, so it has
 * no check-in or outcome, and it is released by itself once its time passes.
 */
const isTest = (b) => !!b.is_test;

/** The customer's invite page: add to calendar, .ics, and the join link for an online service. */
async function copyInvite(url) {
  try {
    await navigator.clipboard.writeText(url);
    toast(tr("Invite link copied"));
  } catch {
    // No clipboard permission (or an insecure context): open it so staff can copy the address.
    window.open(url, "_blank", "noopener");
  }
}

export function BookingsPage({ t }) {
  const today = localDate(new Date(), t.timezone);
  const [from, setFrom] = useState(today);
  const to = addDays(from, 7);
  const [data, reload] = useLoad(() => get(`${t.base}/bookings?from=${from}&to=${to}`), [t.id, from]);
  const [creating, setCreating] = useState(false);
  const [summarising, setSummarising] = useState(null);
  const [moving, setMoving] = useState(null);
  const [busy, run] = useAction();

  const act = async (id, path, body, msg) => {
    await run(() => post(`${t.base}/bookings/${id}/${path}`, body), msg);
    reload();
  };
  const attended = async (b) => {
    await act(b.id, "outcome", { status: "completed" }, tr("Marked as attended"));
    // Straight on to the summary while the visit is fresh; it can be closed and written later.
    setSummarising({ ...b, status: "completed" });
  };
  const arrived = async (b) => {
    const res = await run(() => post(`${t.base}/bookings/${b.id}/arrived`));
    if (res) toast(tr(CHECK_IN_TOAST[res.progress] ?? "Checked in"), res.progress === "not_reached" ? "error" : "ok");
    reload();
  };

  const groups = {};
  for (const b of data?.bookings ?? []) (groups[localDate(new Date(b.start_at), t.timezone)] ??= []).push(b);
  const now = Date.now();

  return html`<div>
    <${PageHeader} title=${tr("Bookings")} subtitle=${tr("{from} → {to} · {tz}", { from, to: addDays(to, -1), tz: t.timezone })}>
      <button class="btn" onClick=${() => setFrom(addDays(from, -7))}>${tr("← Previous")}</button>
      <button class="btn" onClick=${() => setFrom(today)}>${tr("This week")}</button>
      <button class="btn" onClick=${() => setFrom(addDays(from, 7))}>${tr("Next →")}</button>
      <button class="btn primary" onClick=${() => setCreating(true)}>${tr("New booking")}</button>
    </${PageHeader}>
    <${PageGuide} id="bookings" />
    ${!data && html`<${Loading} />`}
    ${data && !data.bookings.length && html`<${Empty} title=${tr("No bookings this week")}>${tr("Bookings made by the AI agent or your team show up here.")}</${Empty}>`}
    ${Object.entries(groups).map(([day, list]) => html`<section class="card day">
      <h2>${fmtDay(list[0].start_at, t.timezone)}</h2>
      <table class="table stack">
        <tbody>${list.map((b) => html`<tr class=${b.status === "cancelled" || isTest(b) ? "dim" : ""}>
          <td class="nowrap"><strong>${fmtTime(b.start_at, t.timezone)}</strong>–${fmtTime(b.end_at, t.timezone)}</td>
          <td>${b.service_name}<div class="muted small">${b.resource_name}${b.location_kind === "phone" ? html` · <strong>${tr("Phone call: call the customer")}</strong>` : b.location_kind === "online" ? ` · ${tr("Video call")}` : ""}</div>${b.meeting_url && html`<a class="muted small" dir="ltr" href=${b.meeting_url} target="_blank" rel="noopener">${tr("Join the meeting")}</a>`}</td>
          <td>${b.customer_name || b.contact_name || "—"}<div class="muted small">${b.contact_phone ?? ""} ${b.channel_type ? CHANNEL_LABEL[b.channel_type] : ""}</div></td>
          <td>${isTest(b) && html`<${Badge} kind="warn">${tr("Test booking")}</${Badge}> `}<${Badge} kind=${statusKind[b.status]}>${statusLabel(b.status)}</${Badge}> ${b.source === "agent" ? html`<${Badge} kind="accent">${tr("AI")}</${Badge}>` : ""}
            ${b.arrived_at ? html` <${Badge} kind="accent">${tr("Arrived {time}", { time: fmtTime(b.arrived_at, t.timezone) })}</${Badge}>` : b.customer_confirmed_at && b.status === "confirmed" ? html` <${Badge} kind="ok">${tr("Customer confirmed")}</${Badge}>` : unconfirmedSoon(b, now) ? html` <${Badge} kind="warn">${tr("Not confirmed yet")}</${Badge}>` : ""}
            ${b.visit_summary && html` <${Badge} kind="neutral">${tr("Summary ready")}</${Badge}>`}</td>
          <td class="row-actions">
            ${b.status === "confirmed" && !isTest(b) && !b.arrived_at && localDate(new Date(b.start_at), t.timezone) === today && html`
              <button class="btn small primary" disabled=${busy} onClick=${() => arrived(b)}>${tr("Arrived")}</button>`}
            ${b.status === "confirmed" && b.arrived_at && !b.progress_sent_at && b.has_previous_summary && localDate(new Date(b.start_at), t.timezone) === today && html`
              <button class="btn small" disabled=${busy} onClick=${() => arrived(b)}>${tr("Resend summary")}</button>`}
            ${b.status === "confirmed" && !isTest(b) && Date.parse(b.start_at) < now && html`
              <button class="btn small" disabled=${busy} onClick=${() => attended(b)}>${tr("Attended")}</button>
              <button class="btn small" disabled=${busy} onClick=${() => act(b.id, "outcome", { status: "no_show" }, tr("Marked as no-show"))}>${tr("No-show")}</button>`}
            ${b.status === "completed" && html`
              <button class="btn small ${b.visit_summary ? "ghost" : ""}" onClick=${() => setSummarising(b)}>${b.visit_summary ? tr("Edit visit summary") : tr("Write visit summary")}</button>`}
            ${b.invite_url && b.status !== "cancelled" && html`
              <button class="btn small ghost" onClick=${() => copyInvite(b.invite_url)}>${tr("Copy invite link")}</button>`}
            ${canReschedule(b, now) && html`
              <button class="btn small ghost" disabled=${busy} onClick=${() => setMoving(b)}>${tr("Reschedule")}</button>`}
            ${b.status === "confirmed" && Date.parse(b.start_at) >= now && html`
              <button class="btn small ghost" disabled=${busy} onClick=${() => confirm(tr("Cancel this booking?")) && act(b.id, "cancel", {}, tr("Booking cancelled"))}>${tr("Cancel")}</button>`}
          </td>
        </tr>`)}</tbody>
      </table>
    </section>`)}
    <${Waitlist} t=${t} />
    ${creating && html`<${NewBooking} t=${t} onClose=${() => setCreating(false)} onCreated=${() => { setCreating(false); reload(); }} />`}
    ${moving && html`<${Reschedule} t=${t} booking=${moving} onClose=${() => setMoving(null)} onMoved=${() => { setMoving(null); reload(); }} />`}
    ${summarising && html`<${VisitSummary} t=${t} booking=${summarising} onClose=${() => setSummarising(null)} onSaved=${() => { setSummarising(null); reload(); }} />`}
  </div>`;
}

/**
 * Moves a booking to another free time: the same availability the agent and the New booking form see,
 * a week at a time from the booking's current day. The server keeps the same staff member when they
 * are free and updates the calendar event, reminders and invite page.
 */
function Reschedule({ t, booking, onClose, onMoved }) {
  // Opens on the booking's own week; Previous goes back as far as today, for moving a visit earlier.
  const today = localDate(new Date(), t.timezone);
  const [from, setFrom] = useState(localDate(new Date(booking.start_at), t.timezone));
  const to = addDays(from, 6);
  const [slots, reloadSlots, loadError] = useLoad(() => get(`${t.base}/availability?service_id=${booking.service_id}&from=${from}&to=${to}`), [booking.id, from]);
  const [start, setStart] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const current = localDateTime(new Date(booking.start_at), t.timezone);

  const days = {};
  for (const s of slots?.slots ?? []) if (s.start !== current) (days[s.start.slice(0, 10)] ??= new Set()).add(s.start);

  const move = async () => {
    setBusy(true);
    setError("");
    try {
      await post(`${t.base}/bookings/${booking.id}/reschedule`, { start });
      toast(tr("Booking moved"));
      onMoved();
    } catch (err) {
      // The server's message is written for the agent; staff get the code in words.
      setError(tr(bookingErrorText(err)));
      if (err?.code === "slot_unavailable") {
        setStart("");
        reloadSlots();
      }
    } finally {
      setBusy(false);
    }
  };
  const page = (n) => {
    const next = addDays(from, n);
    setFrom(next < today ? today : next);
    setStart("");
    setError("");
  };
  // A slot is local YYYY-MM-DDTHH:mm; its day name is read at midday UTC so no zone shifts the date.
  const dayName = (day) => fmtDay(`${day}T12:00:00Z`, "UTC");

  return html`<${Modal} title=${tr("Reschedule")} onClose=${onClose}>
    <p class="muted small">${booking.service_name} · ${booking.customer_name || booking.contact_name || ""} · ${tr("now {when}", { when: fmtDateTime(booking.start_at, t.timezone) })}</p>
    <div class="row-actions">
      <button type="button" class="btn small" disabled=${from <= today} onClick=${() => page(-7)}>${tr("← Previous")}</button>
      <span class="muted small">${tr("{from} → {to}", { from, to })}</span>
      <button type="button" class="btn small" onClick=${() => page(7)}>${tr("Next →")}</button>
    </div>
    ${loadError
      ? html`<div class="callout error">${tr("The free times couldn't be loaded. If this service was switched off in Services & staff, turn it back on first.")}</div>`
      : !slots
      ? html`<${Loading} />`
      : Object.keys(days).length
        ? Object.entries(days).map(([day, set]) => html`<div class="field"><span class="label">${dayName(day)}</span>
            <div class="slot-grid">${[...set].map((s) => html`<button type="button" class="slot ${start === s ? "active" : ""}" aria-pressed=${start === s} onClick=${() => { setStart(s); setError(""); }}><span dir="ltr">${s.slice(11)}</span></button>`)}</div>
          </div>`)
        : html`<p class="muted">${tr("No free times this week. Try the next one.")}</p>`}
    ${error && html`<div class="callout error" role="alert">${error}</div>`}
    <button type="button" class="btn primary full" disabled=${busy || !start} onClick=${move}>${start ? tr("Move to {when}", { when: `${dayName(start.slice(0, 10))} ${start.slice(11)}` }) : tr("Pick a new time")}</button>
  </${Modal}>`;
}

/**
 * After the visit: staff's own notes (never sent), the AI's draft of what the customer will read, and
 * staff's approval. The customer gets the approved summary when they check in next time, and it
 * stays on their progress page.
 */
function VisitSummary({ t, booking, onClose, onSaved }) {
  const [notes, setNotes] = useState(booking.visit_notes ?? "");
  const [summary, setSummary] = useState(booking.visit_summary ?? "");
  const [busy, run] = useAction();
  const draft = async () => {
    const res = await run(() => post(`${t.base}/bookings/${booking.id}/visit-summary/draft`, { notes }));
    if (res?.summary) setSummary(res.summary);
  };
  const save = async (e) => {
    e.preventDefault();
    const res = await run(() => put(`${t.base}/bookings/${booking.id}/visit-summary`, { notes, summary }), summary.trim() ? tr("Summary approved") : tr("Notes saved"));
    if (res) onSaved();
  };
  return html`<${Modal} title=${tr("Visit summary")} onClose=${onClose}>
    <p class="muted small">${booking.service_name} · ${booking.customer_name || booking.contact_name || ""} · ${fmtDateTime(booking.start_at, t.timezone)}</p>
    <form onSubmit=${save}>
      <${Field} label=${tr("Your notes")} hint=${tr("For the team only. Never sent to the customer.")}>
        <textarea rows="4" maxlength="4000" value=${notes} onInput=${(e) => setNotes(e.target.value)} placeholder=${tr("What was done, how it went, what's next")}></textarea>
      </${Field}>
      <button type="button" class="btn small" disabled=${busy || !notes.trim()} onClick=${draft}>${tr("Draft the customer's summary with AI")}</button>
      <${Field} label=${tr("What the customer sees")} hint=${tr("Sent when they check in for their next visit, and kept on their progress page. Check it before you approve.")}>
        <textarea rows="5" maxlength="1000" value=${summary} onInput=${(e) => setSummary(e.target.value)}></textarea>
      </${Field}>
      <button class="btn primary full" disabled=${busy}>${summary.trim() ? tr("Approve summary") : tr("Save notes")}</button>
    </form>
  </${Modal}>`;
}

const TOD = { morning: "mornings", afternoon: "afternoons", evening: "evenings", any: "any time" };

function Waitlist({ t }) {
  const [data, reload] = useLoad(() => get(`${t.base}/waitlist`), [t.id]);
  const [busy, run] = useAction();
  if (!data?.entries.length) return null;
  return html`<section class="card">
    <h2>${tr("Waitlist")} <span class="muted small">${data.entries.length}</span></h2>
    <p class="muted small">${tr("Customers waiting for a time to open. When a booking is cancelled or moved, the agent offers the slot to matching customers.")}</p>
    <table class="table stack"><tbody>${data.entries.map((w) => html`<tr>
      <td>${w.conversation_id ? html`<a href=${`/inbox/${w.conversation_id}`}>${w.contact_name || w.contact_phone || tr("Customer")}</a>` : w.contact_name || tr("Customer")}</td>
      <td>${w.service_name}${w.resource_name ? html`<div class="muted small">${tr("with {name}", { name: w.resource_name })}</div>` : ""}</td>
      <td class="small">${w.from_date} → ${w.to_date}<div class="muted">${tr(TOD[w.time_of_day])}</div></td>
      <td>${w.status === "offered" ? html`<${Badge} kind="accent">${tr("offered {n}×", { n: w.offers_sent })}</${Badge}>` : html`<${Badge} kind="neutral">${tr("waiting")}</${Badge}>`}</td>
      <td class="row-actions"><button class="btn small ghost" disabled=${busy} onClick=${async () => { await run(() => del(`${t.base}/waitlist/${w.id}`), tr("Removed from waitlist")); reload(); }}>${tr("Remove")}</button></td>
    </tr>`)}</tbody></table>
  </section>`;
}

function NewBooking({ t, onClose, onCreated }) {
  const [services] = useLoad(() => get(`${t.base}/services`), [t.id]);
  const [serviceId, setServiceId] = useState("");
  const [day, setDay] = useState(localDate(new Date(), t.timezone));
  const [start, setStart] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  // A cleared date input gives "", which the server refuses; ask only once there is a day to ask about.
  const [slots, , slotsError] = useLoad(() => (serviceId && day ? get(`${t.base}/availability?service_id=${serviceId}&from=${day}&to=${day}`) : Promise.resolve(null)), [serviceId, day]);
  const [busy, run] = useAction();
  const submit = async (e) => {
    e.preventDefault();
    await run(() => post(`${t.base}/bookings`, { service_id: serviceId, start, customer_name: name || undefined, phone: phone || undefined, notes: notes || undefined }), tr("Booked"));
    onCreated();
  };
  return html`<${Modal} title=${tr("New booking")} onClose=${onClose}>
    <form onSubmit=${submit}>
      <${Field} label=${tr("Service")}>
        <select required value=${serviceId} onChange=${(e) => { setServiceId(e.target.value); setStart(""); }}>
          <option value="">${tr("Choose…")}</option>
          ${services?.services.filter((s) => s.active).map((s) => html`<option value=${s.id}>${tr("{name} ({n} min)", { name: s.name, n: s.duration_min })}</option>`)}
        </select>
      </${Field}>
      <${Field} label=${tr("Day")}><input type="date" required value=${day} onInput=${(e) => { setDay(e.target.value); setStart(""); }} /></${Field}>
      ${serviceId && day && html`<div class="field"><span class="label">${tr("Time")}</span>
        ${slotsError ? html`<div class="callout error">${tr("The free times couldn't be loaded. If this service was switched off in Services & staff, turn it back on first.")}</div>` : !slots ? html`<${Loading} />` : slots.slots.length ? html`<div class="slot-grid">${slots.slots.map((s) => html`<button type="button" class="slot ${start === s.start ? "active" : ""}" onClick=${() => setStart(s.start)}>${s.start.slice(11)}</button>`)}</div>` : html`<p class="muted">${tr("No free times that day.")}</p>`}
      </div>`}
      <${Field} label=${tr("Customer name")}><input value=${name} onInput=${(e) => setName(e.target.value)} /></${Field}>
      <${Field} label=${tr("Customer's mobile")} hint=${tr("They get a confirmation now and a reminder the day before.")}><input type="tel" dir="ltr" value=${phone} onInput=${(e) => setPhone(e.target.value)} placeholder="050-123-4567" /></${Field}>
      <${Field} label=${tr("Notes")}><textarea rows="2" value=${notes} onInput=${(e) => setNotes(e.target.value)}></textarea></${Field}>
      <button class="btn primary full" disabled=${busy || !start}>${tr("Book")}</button>
    </form>
  </${Modal}>`;
}
